<template>
  <div class="obj-item" :class="{ 'obj-item--selected': isSelected }">
    <!-- Scenario header -->
    <div class="obj-item-header">
      <label class="obj-item-label-row">
        <input type="radio" :key="`obj-radio-${isSelected}`" :checked="isSelected" @click.prevent="$emit('select')" class="obj-radio" />
        <template v-if="isRenaming">
          <input
            ref="renameInput"
            v-model="renameValue"
            class="obj-rename-input"
            @keydown.enter.prevent="confirmRename"
            @keydown.esc="cancelRename"
          />
          <button class="obj-icon-btn obj-icon-btn--confirm" @click.prevent="confirmRename">
            <b-icon icon="check" />
          </button>
          <button class="obj-icon-btn obj-icon-btn--cancel" @click.prevent="cancelRename">
            <b-icon icon="x" />
          </button>
        </template>
        <span v-else class="obj-label-text" @dblclick="startRename">{{ objective.label }}</span>
      </label>

      <transition name="pill-fade">
        <span v-if="!isOpen && !isRenaming" :class="['obj-mode-pill', modePill.cls]">
          {{ modePill.label }}
        </span>
      </transition>

      <div class="obj-item-actions">
        <button v-if="!isRenaming" class="obj-icon-btn obj-icon-btn--danger" @click="requestDelete" title="Excluir objetivo">
          <b-icon icon="trash" />
        </button>
        <button class="obj-icon-btn" @click="isOpen = !isOpen">
          <b-icon :icon="isOpen ? 'chevron-down' : 'chevron-right'" />
        </button>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <ConfirmDeleteModal
      :visible="showDeleteModal"
      :message="`Tem certeza que deseja excluir o objetivo &quot;${objective.label}&quot;?`"
      :variables="deleteVariables"
      :restrictions="deleteRestrictions"
      @confirm="$emit('remove'); showDeleteModal = false"
      @cancel="showDeleteModal = false"
    />

    <!-- Expanded body -->
    <transition name="card-slide">
    <div v-show="isOpen" class="obj-item-body">
      <!-- Valor Fixo -->
      <div class="obj-mode-row" :class="{ 'obj-mode-row--inactive': objective.mode !== 'fixed' }">
        <input
          type="radio"
          :checked="objective.mode === 'fixed'"
          @change="update({ mode: 'fixed' })"
          class="obj-radio"
        />
        <div class="obj-mode-row-content">
          <span class="obj-mode-label" @click="update({ mode: 'fixed' })">Valor Fixo</span>
          <input
            type="number"
            step="any"
            :value="objective.fixedValue"
            @input="update({ fixedValue: parseFloat($event.target.value) })"
            :disabled="objective.mode !== 'fixed'"
            class="obj-fixed-input"
          />
        </div>
      </div>

      <!-- Otimizar -->
      <div class="obj-mode-row" :class="{ 'obj-mode-row--inactive': objective.mode !== 'optimize' }">
        <input
          type="radio"
          :checked="objective.mode === 'optimize'"
          @change="update({ mode: 'optimize' })"
          class="obj-radio"
        />
        <div class="obj-mode-row-content obj-mode-row-content--column">
          <!-- Custom animated range -->
          <div class="obj-range-wrap" :class="{ 'obj-range-wrap--disabled': objective.mode !== 'optimize' }" @click="toggleOptimizeType">
            <div class="obj-range-track">
              <div class="obj-range-fill" :class="{ 'obj-range-fill--max': objective.optimizeType === 'max' || objective.mode !== 'optimize' }" />
              <div
                class="obj-range-thumb"
                :class="{ 'obj-range-thumb--max': objective.optimizeType === 'max' || objective.mode !== 'optimize' }"
              />
            </div>
          </div>
          <div class="obj-range-labels">
            <span
              class="obj-range-label"
              :class="{ 'obj-range-label--active': objective.optimizeType === 'min' && objective.mode === 'optimize' }"
              @click="objective.mode === 'optimize' && update({ optimizeType: 'min' })"
            >Minimizar</span>
            <span
              class="obj-range-label"
              :class="{ 'obj-range-label--active': (objective.optimizeType === 'max' || objective.mode !== 'optimize') }"
              @click="objective.mode === 'optimize' && update({ optimizeType: 'max' })"
            >Maximizar</span>
          </div>
        </div>
      </div>

      <!-- Origem / Corrente|Operação / Propriedade / Atributo -->
      <div class="obj-corrente-row">
        <label class="obj-corrente-label">Variável Objetivo</label>
        <div v-if="correnteConfirmed" class="obj-corrente-confirmed" @click="correnteConfirmed = false">
          <b-icon icon="broadcast" class="obj-corrente-confirmed-icon" />
          <span class="obj-corrente-confirmed-text">
            <template v-if="objective.correnteLabel">{{ objective.correnteLabel }}</template>
            <template v-else-if="objective.origin === 'Operação'">
              {{ (objective.operation || '').replace('|', ' / ') }}
              <template v-if="objective.operationAtributo"> / {{ objective.operationAtributo }}</template>
            </template>
            <template v-else>
              {{ objective.corrente }}
              <template v-if="objective.propriedade"> / {{ objective.propriedade.name }}</template>
              <template v-if="objective.atributo"> / {{ objective.atributo }}</template>
            </template>
          </span>
          <b-icon icon="pencil" class="obj-corrente-edit-icon" />
        </div>
        <template v-else>
          <div v-if="!singletonLoaded" class="obj-corrente-warning">
            <b-icon icon="exclamation-triangle" class="obj-corrente-warning-icon" />
            <span>Execute uma simulação para habilitar a seleção.</span>
          </div>
          <label class="obj-corrente-sublabel">Origem</label>
          <div class="origin-toggle">
            <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': (objective.origin || 'Corrente') === 'Corrente' }" @click="setOrigin('Corrente')">Corrente</button>
            <button type="button" class="origin-toggle-btn" :class="{ 'origin-toggle-btn--active': objective.origin === 'Operação' }" @click="setOrigin('Operação')">Operação</button>
          </div>
          <template v-if="(objective.origin || 'Corrente') === 'Corrente'">
            <label class="obj-corrente-sublabel">Corrente</label>
            <SearchableSelect
              :modelRef="objective.corrente"
              :optList="correnteList"
              optValue="name"
              optDisplay="name"
              :inputFunc="setCorrente"
              placeholder="Selecione uma corrente..."
              :clearable="true"
              extraClass="obj-corrente-select"
            />
            <template v-if="objective.corrente">
              <label class="obj-corrente-sublabel">Propriedade</label>
              <SearchableSelect
                :modelRef="objective.propriedade ? objective.propriedade.name : null"
                :optList="propriedadeList"
                optValue="name"
                optDisplay="name"
                :inputFunc="setPropriedade"
                placeholder="Selecione uma propriedade..."
                :clearable="true"
                extraClass="obj-corrente-select"
              />
            </template>
            <template v-if="objective.propriedade">
              <label class="obj-corrente-sublabel">Atributo</label>
              <SearchableSelect
                :modelRef="objective.atributo"
                :optList="atributoList"
                optValue="key"
                optDisplay="key"
                :inputFunc="setAtributo"
                placeholder="Selecione um atributo..."
                :clearable="true"
                extraClass="obj-corrente-select"
              />
            </template>
          </template>
          <template v-else>
            <label class="obj-corrente-sublabel">Operação</label>
            <SearchableSelect
              :modelRef="objective.operation"
              :optList="operationList"
              optValue="value"
              optDisplay="display"
              :inputFunc="setOperation"
              placeholder="Selecione uma operação..."
              :clearable="true"
              extraClass="obj-corrente-select"
            />
            <template v-if="objective.operation">
              <label class="obj-corrente-sublabel">Atributo</label>
              <SearchableSelect
                :modelRef="objective.operationAtributo"
                :optList="operationAtributoList"
                optValue="key"
                optDisplay="key"
                :inputFunc="setOperationAtributo"
                placeholder="Selecione um atributo..."
                :clearable="true"
                extraClass="obj-corrente-select"
              />
            </template>
          </template>
          <template v-if="hasObjectiveSource">
            <label class="obj-corrente-sublabel">Nome Personalizado (opcional)</label>
            <input
              v-model="correnteApelidoTemp"
              class="obj-apelido-input"
              placeholder="Ex: Caldo Clarificado..."
            />
          </template>
          <template v-if="objective.mode !== 'fixed'">
            <label class="obj-corrente-sublabel" style="margin-top: 6px;" :title="objectiveMinMaxHint">
              Mínimo / Máximo
            </label>
            <div class="obj-objective-range">
              <input
                type="number"
                step="any"
                :value="objective.min ?? null"
                @input="update({ min: $event.target.value === '' ? null : parseFloat($event.target.value) })"
                class="obj-apelido-input"
                :class="{ 'obj-input-error': isObjectiveMinInvalid }"
                placeholder="Mínimo"
                :title="objective.optimizeType === 'min' ? 'Valor ideal (alvo do solver no modo Minimizar)' : 'Limite inferior aceitável'"
              />
              <input
                type="number"
                step="any"
                :value="objective.max ?? null"
                @input="update({ max: $event.target.value === '' ? null : parseFloat($event.target.value) })"
                class="obj-apelido-input"
                :class="{ 'obj-input-error': isObjectiveMaxInvalid }"
                placeholder="Máximo"
                :title="objective.optimizeType === 'max' ? 'Valor ideal (alvo do solver no modo Maximizar)' : 'Limite superior aceitável'"
              />
            </div>
            <span v-if="isObjectiveRangeInverted" class="obj-field-error">Mínimo deve ser menor que Máximo.</span>
          </template>
          <label class="obj-corrente-sublabel" style="margin-top: 6px;">Penalty Factor</label>
          <input
            type="number"
            step="any"
            :value="objective.penaltyFactor ?? 1"
            @input="update({ penaltyFactor: parseFloat($event.target.value) })"
            class="obj-apelido-input"
            placeholder="1"
            title="Peso relativo do objetivo no penalty total (default 1)"
          />
          <template v-if="hasObjectiveSource">
            <button
              class="obj-corrente-confirm-btn"
              :disabled="!objectiveSourceComplete"
              @click="confirmCorrente"
            >
              <b-icon icon="check" class="mr-1" /> Confirmar
            </button>
          </template>
        </template>
      </div>

      <!-- Variáveis & Restrições (área única implícita — sem UI de "áreas") -->
      <div class="obj-areas">
        <RtoObjectiveArea
          v-if="defaultArea"
          :area="defaultArea"
          @update="updateArea($event)"
        />
      </div>

      <!-- Controles avançados do solver — só visíveis com permissão `rtoconfigavancada` (CA-02.2) -->
      <template v-if="hasAvancadaPermission">
      <!-- Grid pré-screen (LHS) — por objetivo -->
      <div class="obj-solver-row">
        <label class="obj-solver-toggle">
          <input type="checkbox" :checked="objective.usarGrid !== false" @change="update({ usarGrid: $event.target.checked })" />
          <span>Grid pré-screen (LHS)</span>
        </label>
        <span class="obj-solver-hint">Sondagem inicial quasi-aleatória antes do LM</span>
      </div>
      <div v-if="objective.usarGrid !== false" class="obj-solver-grid-row">
        <div class="obj-solver-field">
          <label class="obj-solver-label">Tamanho do Grid</label>
          <input
            type="number"
            :value="objective.tamanhoGrid ?? 10"
            @input="update({ tamanhoGrid: parseInt($event.target.value, 10) })"
            class="obj-apelido-input"
            :class="{ 'obj-input-error': isTamanhoGridInvalid }"
            placeholder="10"
          />
          <span v-if="isTamanhoGridInvalid" class="obj-field-error">Deve ser um inteiro maior que 0</span>
        </div>
        <div class="obj-solver-field">
          <label class="obj-solver-label">Multi-start K</label>
          <input
            type="number"
            :value="objective.multiStartK ?? 3"
            @input="update({ multiStartK: parseInt($event.target.value, 10) })"
            class="obj-apelido-input"
            :class="{ 'obj-input-error': isMultiStartKInvalid }"
            placeholder="3"
          />
          <span v-if="isMultiStartKInvalid" class="obj-field-error">Deve ser um inteiro maior que 0</span>
        </div>
      </div>
      <div v-if="multiStartGreaterThanGrid" class="obj-solver-warning">
        <b-icon icon="exclamation-triangle-fill" class="mr-1" />
        Multi-start K maior que o Tamanho do Grid — só os top-K do grid serão usados.
      </div>

      <!-- Limitar tempo máximo — por objetivo -->
      <div class="obj-solver-row">
        <label class="obj-solver-toggle">
          <input type="checkbox" :checked="objective.maxTempoEnable !== false" @change="update({ maxTempoEnable: $event.target.checked })" />
          <span>Limitar tempo máximo</span>
        </label>
      </div>
      <div v-if="objective.maxTempoEnable !== false" class="obj-solver-grid-row obj-solver-grid-row--single">
        <div class="obj-solver-field">
          <label class="obj-solver-label">Tempo máx. (s)</label>
          <input
            type="number"
            :value="objective.maxTempoSegundos ?? 300"
            @input="update({ maxTempoSegundos: parseInt($event.target.value, 10) })"
            class="obj-apelido-input"
            :class="{ 'obj-input-error': isMaxTempoSegundosInvalid }"
            placeholder="300"
          />
          <span v-if="isMaxTempoSegundosInvalid" class="obj-field-error">Deve ser um inteiro maior que 0</span>
        </div>
      </div>

      <!-- Polish step — por objetivo -->
      <div class="obj-solver-row">
        <label class="obj-solver-toggle">
          <input type="checkbox" :checked="objective.usarPolish !== false" @change="update({ usarPolish: $event.target.checked })" />
          <span>Polish step (±5%)</span>
        </label>
        <span class="obj-solver-hint">Sondagem ao redor da solução final para escapar de saturação</span>
      </div>
      </template>

      <!-- Gatilhos para atuação do RTO (por objetivo — N gatilhos, cada um Ativo/Inativo) -->
      <div class="obj-gatilhos-section">
        <div class="obj-gatilhos-title">
          <b-icon icon="lightning" class="mr-1" />
          <span>Gatilhos para atuação do RTO</span>
          <span v-if="gatilhos.length > 0" class="obj-gatilhos-count">{{ gatilhos.length }}</span>
        </div>

        <p v-if="gatilhos.length === 0" class="obj-gatilhos-empty">Nenhum gatilho configurado.</p>

        <div v-for="gatilho in gatilhos" :key="gatilho.id" class="obj-gatilho-row">
          <span
            class="obj-gatilho-dot"
            :class="gatilho.status === 'Ativo' ? 'obj-gatilho-dot--ativo' : 'obj-gatilho-dot--inativo'"
            :title="gatilho.status === 'Ativo' ? 'Ativo' : 'Inativo'"
          />
          <span class="obj-gatilho-nome" :title="gatilho.nome">{{ gatilho.nome || 'Sem nome' }}</span>
          <span class="obj-gatilho-elem">{{ (gatilho.elementos || []).length }} elem.</span>
          <div class="obj-gatilho-actions">
            <button
              class="obj-icon-btn"
              :disabled="triggerDisabled"
              title="Editar gatilho"
              @click="$emit('open-gatilho', gatilho.id)"
            >
              <b-icon icon="pencil" />
            </button>
            <button
              class="obj-icon-btn"
              :disabled="triggerDisabled"
              :title="gatilho.status === 'Ativo' ? 'Desativar gatilho' : 'Ativar gatilho'"
              @click="$emit('toggle-gatilho-status', gatilho.id)"
            >
              <b-icon :icon="gatilho.status === 'Ativo' ? 'toggle-on' : 'toggle-off'" />
            </button>
            <button
              class="obj-icon-btn obj-icon-btn--danger"
              :disabled="triggerDisabled"
              title="Excluir gatilho"
              @click="$emit('remove-gatilho', gatilho.id)"
            >
              <b-icon icon="trash" />
            </button>
          </div>
        </div>

        <button
          class="obj-add-dashed-btn"
          :disabled="triggerDisabled"
          :title="triggerDisabled ? 'Sem permissão para editar' : 'Adicionar gatilho'"
          @click="$emit('add-gatilho')"
        >
          <b-icon icon="plus" class="mr-1" /> Adicionar gatilho
        </button>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import RtoObjectiveArea from './RtoObjectiveArea.vue';
import ConfirmDeleteModal from './ConfirmDeleteModal.vue';
import SearchableSelect from '../rta/commons/SearchableSelect.vue';
import { SingletonEnginnering } from '../flowchart/nodes/_service/singletonEngineering';
import { safeRandomUUID } from '@/services/uuid';

export default {
  name: 'RtoObjectiveItem',

  components: { RtoObjectiveArea, ConfirmDeleteModal, SearchableSelect },

  props: {
    objective: { type: Object, required: true },
    isSelected: { type: Boolean, default: false },
    triggerDisabled: { type: Boolean, default: false },
    hasAvancadaPermission: { type: Boolean, default: false },
  },

  emits: ['select', 'update', 'remove', 'add-gatilho', 'open-gatilho', 'toggle-gatilho-status', 'remove-gatilho'],

  data() {
    return {
      isOpen: false,
      isRenaming: false,
      renameValue: '',
      showDeleteModal: false,
      correnteConfirmed: !!this.objective.corrente || !!this.objective.operation,
      correnteApelidoTemp: this.objective.correnteLabel || '',
    };
  },

  created() {
    // Garante que sempre exista a área única (objetivos legados podem ter areas: [])
    if (!this.objective.areas || this.objective.areas.length === 0) {
      this.update({
        areas: [{ id: safeRandomUUID(), label: 'Padrão', variables: [], restrictions: [] }],
      });
    }
  },

  computed: {
    modePill() {
      if (this.objective.mode === 'fixed') return { label: `Fixo: ${this.objective.fixedValue}`, cls: 'obj-mode-pill--fixed' };
      if (this.objective.optimizeType === 'min') return { label: 'Min', cls: 'obj-mode-pill--min' };
      return { label: 'Max', cls: 'obj-mode-pill--max' };
    },

    singletonLoaded() {
      return SingletonEnginnering.isLoaded && !!SingletonEnginnering.currents;
    },

    correnteList() {
      if (!this.singletonLoaded) return [];
      return SingletonEnginnering.currents.map(c => ({ name: c }));
    },

    propriedadeList() {
      if (!this.objective.corrente) return [];
      try { return SingletonEnginnering.getCurrentProperties(this.objective.corrente); }
      catch { return []; }
    },

    atributoList() {
      if (!this.objective.corrente || !this.objective.propriedade) return [];
      try { return SingletonEnginnering.getCurrentPropertiesAttributes(this.objective.corrente, this.objective.propriedade); }
      catch { return []; }
    },

    operationList() {
      if (!this.singletonLoaded || !SingletonEnginnering.operations) return [];
      return SingletonEnginnering.operations.map(o => ({
        value: o,
        display: o.replace('|', ' / '),
      }));
    },

    operationAtributoList() {
      if (!this.objective.operation) return [];
      try { return SingletonEnginnering.getOperationsProperties(this.objective.operation); }
      catch { return []; }
    },

    isTamanhoGridInvalid() {
      const v = this.objective.tamanhoGrid;
      if (v == null) return false;
      return !v || v < 1 || !Number.isInteger(v);
    },

    isMultiStartKInvalid() {
      const v = this.objective.multiStartK;
      if (v == null) return false;
      return !v || v < 1 || !Number.isInteger(v);
    },

    multiStartGreaterThanGrid() {
      if (this.objective.usarGrid === false) return false;
      const g = this.objective.tamanhoGrid;
      const k = this.objective.multiStartK;
      return g && k && k > g;
    },

    isMaxTempoSegundosInvalid() {
      if (this.objective.maxTempoEnable === false) return false;
      const v = this.objective.maxTempoSegundos;
      if (v == null) return false;
      return !v || v < 1 || !Number.isInteger(v);
    },

    isObjectiveMinInvalid() {
      if (this.objective.mode === 'fixed') return false;
      const v = this.objective.min;
      return v == null || Number.isNaN(Number(v));
    },

    isObjectiveMaxInvalid() {
      if (this.objective.mode === 'fixed') return false;
      const v = this.objective.max;
      return v == null || Number.isNaN(Number(v));
    },

    isObjectiveRangeInverted() {
      if (this.objective.mode === 'fixed') return false;
      const min = this.objective.min;
      const max = this.objective.max;
      if (min == null || max == null) return false;
      return Number(min) >= Number(max);
    },

    objectiveMinMaxHint() {
      if (this.objective.optimizeType === 'min') {
        return 'No modo Minimizar, o solver usa Mínimo como alvo (valor ideal a atingir). Máximo é o limite superior aceitável.';
      }
      return 'No modo Maximizar, o solver usa Máximo como alvo (valor ideal a atingir). Mínimo é o limite inferior aceitável.';
    },

    hasObjectiveSource() {
      return this.objective.origin === 'Operação' ? !!this.objective.operation : !!this.objective.corrente;
    },

    objectiveSourceComplete() {
      if (this.objective.origin === 'Operação') {
        return !!this.objective.operation && !!this.objective.operationAtributo;
      }
      return !!this.objective.corrente && !!this.objective.propriedade && !!this.objective.atributo;
    },

    defaultArea() {
      return (this.objective.areas && this.objective.areas[0]) || null;
    },

    gatilhos() {
      return this.objective.gatilhos || [];
    },

    deleteVariables() {
      const area = this.defaultArea;
      return area ? area.variables.map(v => v.label) : [];
    },

    deleteRestrictions() {
      const area = this.defaultArea;
      return area ? (area.restrictions || []).map(r => r.label) : [];
    },
  },

  methods: {
    toggleOptimizeType() {
      if (this.objective.mode !== 'optimize') return;
      this.update({ optimizeType: this.objective.optimizeType === 'min' ? 'max' : 'min' });
    },

    setOrigin(origin) {
      this.update({
        origin,
        corrente: null,
        propriedade: null,
        atributo: null,
        operation: null,
        operationAtributo: null,
        correnteLabel: '',
      });
      this.correnteApelidoTemp = '';
      this.correnteConfirmed = false;
    },

    setCorrente(e) {
      const corrente = e.target.value || null;
      this.update({ origin: 'Corrente', corrente, correnteLabel: '', propriedade: null, atributo: null });
      if (!corrente) this.correnteApelidoTemp = '';
      this.correnteConfirmed = false;
    },

    setPropriedade(e) {
      const name = e.target.value;
      const propriedade = name ? (this.propriedadeList.find(p => p.name === name) || null) : null;
      this.update({ propriedade, atributo: null });
    },

    setAtributo(e) {
      this.update({ atributo: e.target.value || null });
    },

    setOperation(e) {
      this.update({ origin: 'Operação', operation: e.target.value || null, operationAtributo: null, correnteLabel: '' });
      this.correnteConfirmed = false;
    },

    setOperationAtributo(e) {
      this.update({ operationAtributo: e.target.value || null });
    },

    confirmCorrente() {
      this.update({ correnteLabel: this.correnteApelidoTemp.trim() });
      this.correnteConfirmed = true;
    },

    requestDelete() {
      this.showDeleteModal = true;
    },

    update(changes) {
      this.$emit('update', { ...this.objective, ...changes });
    },

    startRename() {
      this.renameValue = this.objective.label;
      this.isRenaming = true;
      this.$nextTick(() => this.$refs.renameInput?.focus());
    },

    confirmRename() {
      if (this.renameValue.trim()) this.update({ label: this.renameValue.trim() });
      this.isRenaming = false;
    },

    cancelRename() {
      this.isRenaming = false;
    },

    // Atualiza a área única (objetivo tem sempre exatamente 1 área interna).
    updateArea(updated) {
      this.update({
        areas: this.objective.areas.map(a => a.id === updated.id ? updated : a),
      });
    },
  },
};
</script>

<style scoped>
.obj-item {
  border: 1px solid #e5e7eb;
  border-left: 3px solid transparent;
  border-radius: 4px;
  background-color: #ffffff;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  transition: box-shadow 200ms ease, border-color 250ms ease;
}

.obj-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.obj-item--selected {
  border-left-color: #ff5f00;
  box-shadow: 0 2px 8px rgba(255, 95, 0, 0.15);
}

.obj-item--selected:hover {
  box-shadow: 0 3px 12px rgba(255, 95, 0, 0.22);
}

/* --- Header --- */
.obj-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  background-color: #f9fafb;
  transition: background-color 250ms ease;
}

.obj-item--selected .obj-item-header {
  background-color: #fff7ed;
}

.obj-item-header:hover {
  background-color: #f3f4f6;
}

.obj-item--selected .obj-item-header:hover {
  background-color: #ffedd5;
}

.obj-item-label-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  margin: 0;
  cursor: pointer;
  min-width: 0;
}

.obj-radio {
  flex-shrink: 0;
  accent-color: #ff5f00;
  width: 13px;
  height: 13px;
  cursor: pointer;
}

.obj-label-text {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.obj-rename-input {
  font-size: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  padding: 2px 6px;
  width: 120px;
  color: #374151;
}

.obj-rename-input:focus {
  outline: none;
  border-color: #ff5f00;
}

/* --- Actions --- */
.obj-item-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

/* --- Mode pill --- */
.obj-mode-pill {
  flex-shrink: 0;
  font-size: 0.58rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 9px;
  line-height: 1.4;
}

.obj-mode-pill--fixed {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.obj-mode-pill--max {
  background-color: #dcfce7;
  color: #15803d;
}

.obj-mode-pill--min {
  background-color: #ffedd5;
  color: #c2410c;
}

.pill-fade-enter-active,
.pill-fade-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.pill-fade-enter,
.pill-fade-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

.pill-fade-enter-to,
.pill-fade-leave {
  opacity: 1;
  transform: scale(1);
}

.obj-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  border-radius: 3px;
  padding: 3px;
  font-size: 0.78rem;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 100ms;
}

.obj-icon-btn:hover { background-color: #e5e7eb; }
.obj-icon-btn--confirm { color: #16a34a; }
.obj-icon-btn--confirm:hover { background-color: #dcfce7; }
.obj-icon-btn--cancel { color: #6b7280; }
.obj-icon-btn--danger { color: #ef4444; }
.obj-icon-btn--danger:hover { background-color: #fee2e2; }


/* --- Expanded body --- */
.obj-item-body {
  padding: 10px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* --- Mode rows --- */
.obj-mode-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.obj-mode-row--inactive { opacity: 0.45; }

.obj-mode-row-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
}

.obj-mode-row-content--column {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.obj-mode-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.04em;
  cursor: pointer;
}

.obj-fixed-input {
  width: 90px;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  padding: 3px 6px;
  font-size: 0.78rem;
  text-align: right;
  color: #374151;
}

.obj-fixed-input:disabled { background-color: #f9fafb; }
.obj-fixed-input:focus { outline: none; border-color: #ff5f00; }

.obj-range-wrap {
  position: relative;
  padding: 6px 0;
  cursor: pointer;
}

.obj-range-wrap--disabled {
  opacity: 0.45;
  pointer-events: none;
}

.obj-range-track {
  position: relative;
  height: 4px;
  background-color: #e5e7eb;
  border-radius: 2px;
}

.obj-range-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 0%;
  background-color: #ff5f00;
  border-radius: 2px;
  transition: width 250ms ease;
}

.obj-range-fill--max {
  width: 100%;
}

.obj-range-thumb {
  position: absolute;
  top: 50%;
  left: 0%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  background-color: #ff5f00;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: left 250ms ease;
}

.obj-range-thumb--max {
  left: 100%;
}

.obj-range-labels {
  display: flex;
  justify-content: space-between;
}

.obj-range-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  color: #9ca3af;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: color 220ms ease;
}

.obj-range-label--active {
  color: #ff5f00;
}

/* --- Corrente section --- */
.obj-corrente-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.obj-corrente-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 0;
}

.obj-apelido-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 5px 8px;
  font-size: 0.85rem;
  color: #374151;
  background-color: #ffffff;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}

.obj-apelido-input:focus {
  outline: none;
  border-color: #ff5f00;
  box-shadow: 0 0 0 3px rgba(255, 95, 0, 0.15);
}

.obj-corrente-sublabel {
  font-size: 0.65rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 4px 0 2px;
}

.obj-corrente-confirmed {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 150ms;
}

.obj-corrente-confirmed:hover {
  background-color: #ffedd5;
}

.obj-corrente-confirmed-icon {
  color: #ff5f00;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.obj-corrente-confirmed-text {
  flex: 1;
  font-size: 0.78rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.obj-corrente-edit-icon {
  font-size: 0.65rem;
  color: #9ca3af;
  flex-shrink: 0;
}

.obj-corrente-warning {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background-color: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 3px;
  font-size: 0.68rem;
  color: #92400e;
}

.obj-corrente-warning-icon {
  color: #b45309;
  flex-shrink: 0;
  font-size: 0.75rem;
}


.obj-corrente-confirm-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 5px;
  background-color: #ff5f00;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms;
}

.obj-corrente-confirm-btn:hover:not(:disabled) {
  background-color: #e04e00;
}

.obj-corrente-confirm-btn:disabled {
  background-color: #d1d5db;
  cursor: not-allowed;
}

/* --- Areas section --- */
.obj-areas {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* --- Add area inline --- */
.obj-add-area-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.obj-add-area-input {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  padding: 4px 8px;
  font-size: 0.75rem;
  color: #374151;
}

.obj-add-area-input:focus {
  outline: none;
  border-color: #ff5f00;
}

.obj-add-dashed-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 5px;
  background: none;
  border: 1px dashed #d1d5db;
  border-radius: 4px;
  font-size: 0.7rem;
  color: #9ca3af;
  cursor: pointer;
  transition: color 150ms, border-color 150ms;
}

.obj-add-dashed-btn:hover {
  color: #ff5f00;
  border-color: #ff5f00;
}

/* --- Card slide transition --- */
.card-slide-enter-active,
.card-slide-leave-active {
  transition: max-height 280ms ease, opacity 200ms ease;
  overflow: hidden;
}

.card-slide-enter,
.card-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.card-slide-enter-to,
.card-slide-leave {
  max-height: 1200px;
  opacity: 1;
}

.obj-objective-range {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.obj-solver-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 0 4px;
  padding: 0 8px;
  flex-wrap: wrap;
}
.obj-solver-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}
.obj-solver-toggle input {
  margin: 0;
}
.obj-solver-hint {
  font-size: 11px;
  color: #6b7280;
  font-style: italic;
}
.obj-solver-grid-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 0 8px;
  margin-bottom: 4px;
}
.obj-solver-grid-row--single {
  grid-template-columns: 1fr;
}
.obj-solver-field {
  display: flex;
  flex-direction: column;
}
.obj-solver-label {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 2px;
}
.obj-input-error {
  border-color: #ef4444 !important;
}
.obj-field-error {
  font-size: 10px;
  color: #ef4444;
  margin-top: 2px;
}
.obj-solver-warning {
  margin: 4px 8px;
  padding: 6px 10px;
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 4px;
  font-size: 11px;
  color: #92400e;
  display: flex;
  align-items: center;
}

/* --- Gatilhos section --- */
.obj-gatilhos-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 10px 0 4px;
  padding: 0 8px;
}
.obj-gatilhos-title {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}
.obj-gatilhos-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  margin-left: 6px;
  padding: 0 5px;
  background: #f97316;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 8px;
}
.obj-gatilhos-empty {
  font-size: 11px;
  color: #9ca3af;
  font-style: italic;
  margin: 0;
}
.obj-gatilho-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
}
.obj-gatilho-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.obj-gatilho-dot--ativo { background: #16a34a; }
.obj-gatilho-dot--inativo { background: #d1d5db; }
.obj-gatilho-nome {
  flex: 1;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.obj-gatilho-elem {
  flex-shrink: 0;
  font-size: 10px;
  color: #9ca3af;
}
.obj-gatilho-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.obj-icon-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.origin-toggle {
  display: inline-flex;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  width: fit-content;
  margin-bottom: 6px;
}
.origin-toggle-btn {
  border: 0;
  background: #fff;
  color: #374151;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.origin-toggle-btn + .origin-toggle-btn {
  border-left: 1px solid #d1d5db;
}
.origin-toggle-btn:hover {
  background: #f3f4f6;
}
.origin-toggle-btn--active {
  background: #f97316;
  color: #fff;
  font-weight: 600;
}
.origin-toggle-btn--active:hover {
  background: #f97316;
}
</style>
